/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import * as _ from 'lodash';
import { useState } from 'react';
import { Text, Checkbox, TextInput, Upload, uploadSingleFile } from '@blend/enterprise-components';
import { Field } from 'react-final-form';
import { css, StyleSheet } from 'aphrodite';
import { Label } from './Label';
import { Options } from './Options';
import { BlendBridge } from 'blb-types/blendBridge';
import { WrappedInput } from 'blb-components/WrappedInput';
import { UserFileUploader } from '../../ProfilePage/UserFileUploader';
import { uploadFile } from 'blb-api/files';
import { UploadItem } from '../DocUploadModal/UploadItem';

interface TaskDescriptionProps extends React.AllHTMLAttributes<HTMLInputElement> {
  displayInline?: boolean;
  form?: any;
  taskDescription: BlendBridge.API.TaskDescription;
}

const styles = StyleSheet.create({
  block: {
    display: 'block',
  },
  inline: {
    display: 'inline',
  },
});

export const TaskDescription: React.FC<TaskDescriptionProps> = ({
  taskDescription,
  displayInline,
  form,
  ...htmlProps
}: TaskDescriptionProps) => {
  const className = displayInline ? css(styles.inline) : css(styles.block);
  return (
    <div className={className} {...htmlProps}>
      {taskView(taskDescription, form)}
    </div>
  );
};

export const taskView = (task: BlendBridge.API.TaskDescription, form: any): React.ReactElement | null => {
  switch (task.type) {
    // NOTE: brackets are needed around each case to prevent: `Cannot redeclare block-scoped variable` on matching field names like `label`
    case BlendBridge.API.TaskDescriptionTypes.LABEL: {
      const { label, value } = task;
      return <Label label={label} value={value} />;
    }
    case BlendBridge.API.TaskDescriptionTypes.TEXT: {
      const { description, underline, weight } = task;
      return (
        <Text underline={underline} weight={weight}>
          {description}
        </Text>
      );
    }
    case BlendBridge.API.TaskDescriptionTypes.INPUT: {
      const { promptText, options, name } = task;
      return <Options name={name} options={options} promptText={promptText} />;
    }
    case BlendBridge.API.TaskDescriptionTypes.CHECKBOX: {
      const {
        disabled,
        checked,
        label,
        name,
      }: { checked?: boolean; disabled?: boolean; label?: string; name: string } = task;
      if (!name) {
        // prevent <Field> from blowing up if the API result failed to provide a `name` value
        return null;
      }
      return (
        <Field
          component={WrappedInput}
          disabled={disabled}
          initialValue={checked}
          inputComponent={Checkbox}
          label={label}
          labelPosition="right"
          labelSize="sm"
          name={name}
          type="checkbox"
        />
      );
    }
    case BlendBridge.API.TaskDescriptionTypes.TEXT_INPUT: {
      const {
        label,
        disabled,
        name,
        promptText,
      }: { disabled?: boolean; label?: string; name: string; promptText?: string } = task;
      return (
        <Field
          component={WrappedInput}
          disabled={disabled}
          inputComponent={TextInput}
          label={label}
          labelPosition="top"
          labelSize="sm"
          name={name}
          placeholder={promptText}
          type="text"
        />
      );
    }
    case BlendBridge.API.TaskDescriptionTypes.HIDDEN_INPUT: {
      const { name }: { name: string } = task;
      return (
        <Field name={name}>
          {({ input }) => (
            <div>
              <div className="field-wrapper">
                <input {...input} id={`it-Hidden-${name}`} name={name} type="hidden" />
              </div>
            </div>
          )}
        </Field>
      );
    }
    case BlendBridge.API.TaskDescriptionTypes.FILE_INPUT: {
      const { disabled, fileType } = task;
      const [uploadMap, setUploadMap] = useState<Record<string, { file: File; type?: string; upload: Upload }>>({});
      const removeUpload = (fileName: string) => setUploadMap(uploads => _.omit(uploads, fileName));
      const handleUploadEvent = (fileId: string, file: File, upload: Upload) => {
        setUploadMap(uploads => ({
          ...uploads,
          [fileId]: {
            // we specifically overwrite `file` and `upload` and leave
            // everything else as is, e.g. `type`
            ...uploads[fileId],
            file,
            upload,
          },
        }));
      };
      const retryUpload = async (fileId: string) => {
        const { file } = uploadMap[fileId];
        await uploadSingleFile({ file, fileId, handleUploadEvent, otherUploadParams: { fileType }, uploadFile });
      };
      const renderUploadItems = () => {
        return _.map(uploadMap, ({ upload }, fileId) => (
          <UploadItem
            id={fileId}
            key={fileId}
            onRemove={() => removeUpload(fileId)}
            onRetry={async () => await retryUpload(fileId)}
            upload={upload}
          />
        ));
      };
      let acceptFileExts = '.pdf';
      if (fileType === BlendBridge.FileTypes.NOTARY_DIGITAL_CERTIFICATE) {
        acceptFileExts = '.p12';
      }
      return (
        <div>
          {renderUploadItems()}
          <UserFileUploader
            acceptFileExts={acceptFileExts}
            disabled={disabled}
            fileType={fileType}
            form={form}
            handleUploadEvent={handleUploadEvent}
            id="it-FileUploader"
            uploadFile={uploadFile}
          />
        </div>
      );
    }
    default:
      return null;
  }
};
